<template>
  <component
    :is="videoComponent"
    v-if="media.videoUrl"
    :muted="muted"
    :paused="desktopPaused"
    :autoplay="autoplay"
    :src="media.videoUrl"
    :lazy="lazy"
    :poster="getFirst(media.poster) ?? getFirst(media.image)"
    v-bind="$attrs"
    :class="{ '-is-desktop': hasMobile(media) }" />

  <base-image
    v-else-if="exists(media.image)"
    :src="getFirst(media.image).src"
    :webp="getFirst(media.image).webp"
    :avif="getFirst(media.image).avif"
    :width="getFirst(media.image).width"
    :height="getFirst(media.image).height"
    :has-focus="getFirst(media.image).hasFocus"
    :focus="getFirst(media.image).focus"
    :alt="getFirst(media.image).alt"
    :lazy="lazy"
    :fetchpriority="fetchpriority"
    :decoding="decoding"
    :role="role"
    v-bind="$attrs"
    :class="{ '-is-desktop': hasMobile(media) }" />

  <!-- mobile -->
  <component
    :is="videoComponent"
    v-if="media.mobileVideoUrl"
    :muted="muted"
    :paused="mobilePaused"
    :autoplay="autoplay"
    :src="media.mobileVideoUrl"
    :lazy="lazy"
    v-bind="$attrs"
    :poster="getFirst(media.poster) ?? getFirst(media.mobileImage)"
    class="-is-mobile" />

  <base-image
    v-else-if="exists(media.mobileImage)"
    :src="getFirst(media.mobileImage).src"
    :webp="getFirst(media.mobileImage).webp"
    :avif="getFirst(media.mobileImage).avif"
    :width="getFirst(media.mobileImage).width"
    :height="getFirst(media.mobileImage).height"
    :has-focus="getFirst(media.mobileImage).hasFocus"
    :focus="getFirst(media.mobileImage).focus"
    :alt="getFirst(media.mobileImage).alt"
    :lazy="lazy"
    :role="role"
    v-bind="$attrs"
    class="-is-mobile" />
</template>

<script setup>
  import BaseVideo from '~/components/Base/Video'
  import HpVideo from '~/components/HpVideo'

  const props = defineProps({
    media: Object,
    role: String,
    homepage: Boolean,
    paused: Boolean,
    muted: Boolean,
    autoplay: Boolean,
    lazy: {
      type: Boolean,
      default: false
    },
    fetchpriority: {
      type: String,
      default: null
    },
    decoding: {
      type: String,
      default: null
    }
  })

  const layout = useLayoutStore()
  const mounted = useMounted()
  const videoComponent = computed(() => {
    return props.homepage ? HpVideo : BaseVideo
  })

  // layout.mobile is null until the client plugin resolves it. Treat "unknown" as
  // paused on BOTH tracks so neither <video> autoplays/downloads before we know the
  // device — otherwise the desktop clip starts fetching during the SSR/hydration
  // window even on phones. Pages with only a desktop clip (no mobileVideoUrl) keep
  // playing everywhere.
  // The plugin resolves it before hydration, so read it only once mounted: the
  // hydration render must match SSR (unknown), the videos' paused watchers start them.
  const mobile = computed(() => mounted.value ? layout.mobile : null)

  const desktopPaused = computed(() => {
    if (props.paused) { return true }
    return !!props.media.mobileVideoUrl && mobile.value !== false
  })

  const mobilePaused = computed(() => {
    if (props.paused) { return true }
    return mobile.value !== true
  })

  const hasMobile = item => item.mobileVideoUrl || exists(item.mobileImage)
</script>
